import { DriveDocument } from '@/api/types/drive'
import { UserStatus } from '@/api/types/user'
import { WorkstationDocument } from '@/api/types/workstation'
import { getCurrentUserInfo, isLoggedIn } from '@/services/auth'
import store from '@/store/root'
import { NavigationGuard, NavigationGuardNext, Route } from 'vue-router'

// Guards "recipies", returining redirect path if needed
// 1. Ensure logged in / profile setup finished
async function loginRedirects(_to: Route, from: Route): Promise<string | undefined> {
  const { loggedIn, emailVerified } = getCurrentUserInfo()
  if (!loggedIn) {
    return '/'
  }
  if (!emailVerified) {
    return '/verify-email'
  }
  // TODO figure out when to enforce ensureProfile
  if (
    from.path === '/' ||
    from.name === 'shared-link' ||
    !Object.keys(store.getters['user/profile']).length
  ) {
    await store.dispatch('user/ensureProfile')
    store.dispatch('payment/refreshCredits')
  }
  const hasGivenConsent = store.getters['user/hasGivenConsent']
  if (!hasGivenConsent) {
    return '/complete-profile'
  }
  // missing_payment
  const status: UserStatus = store.getters['user/userStatus']
  if (status && status == 'missing_payment') {
    return '/missing-payment'
  }
  // no credits on trial
  const isUserOnTrial: boolean = store.getters['user/isUserOnTrial']
  const creditsInCents: number | undefined = store.getters['payment/creditsInCents']
  if (isUserOnTrial && creditsInCents === 0) {
    return '/no-credits'
  }
  return
}

// 2. Ensure loginRedirects + show setup wizard, if needed
async function toOnboardingRedirects(to: Route, from: Route) {
  const profileRedirect = await loginRedirects(to, from)
  if (profileRedirect) return profileRedirect
  const selectedTier = store.getters['user/selectedTier']
  const hasStoragePlan = Boolean(selectedTier) && Boolean(selectedTier.codename)
  const onboardingSkipped = localStorage?.getItem('skipSetup') === 'true'
  if (!hasStoragePlan && !onboardingSkipped) {
    return '/welcome'
  }
  return
}

/**
 * On the way to /setup...
 * 1. Ensure sign-up finished
 * 2. if already has selectedTier, go to select-plan instead
 * @param to a route
 * @param from a route
 * @returns redirect string or nothing
 */
async function selectPlanRedirect(to: Route, from: Route): Promise<string | undefined> {
  const profileRedirects = await loginRedirects(to, from)
  if (profileRedirects) return profileRedirects

  const selectedTier = store.getters['user/selectedTier']
  const hasStoragePlan = Boolean(selectedTier) && Boolean(selectedTier.codename)

  if (hasStoragePlan) {
    return '/select-plan'
  }
  return
}

/**
 * On the way to /select-plan...
 * 1. Ensure sign-up finished
 * 2. if has no selectedTier, go to /setup instead
 *
 * @param to a route
 * @param from a route
 * @returns redirect string or nothing
 *
 * @note in opposition to toOnboardingRedirects, it does not care
 * onboarding has been skipped.
 */
async function prioritizeSetup(to: Route, from: Route) {
  const profileRedirect = await loginRedirects(to, from)
  if (profileRedirect) return profileRedirect
  const selectedTier = store.getters['user/selectedTier']
  const hasStoragePlan = Boolean(selectedTier) && Boolean(selectedTier.codename)
  if (!hasStoragePlan) {
    return '/welcome'
  }
  return
}

function lastBrowserLocationIfAny() {
  // load previous position in the Browser from vuex when coming from outside
  const selectedDriveUuid = store.getters['file/selectedDriveUuid']
  const path = store.getters['file/path']
  if (selectedDriveUuid) {
    return 'drives/' + selectedDriveUuid + path
  }
  return
}

// Ready to use Guards, "interface"
export const ensureUserLoggedIn: NavigationGuard = async (
  to: Route,
  from: Route,
  next: NavigationGuardNext<Vue>,
) => {
  const possibleRedirect = await loginRedirects(to, from)
  next(possibleRedirect)
}

/**
 * Ensure user is logged in and missing paymentMethod, resources and hasn't skipped the onboarding
 *
 * @param to Route
 * @param from Route
 * @param next NavigationGuardNext
 */
export const redirectToOnboardingIfNeeded: NavigationGuard = async (
  to: Route,
  from: Route,
  next,
) => {
  const possibleRedirect = await toOnboardingRedirects(to, from)
  next(possibleRedirect)
}

/**
 * Prevent from entering the users who are past that.
 *
 * @param to Route
 * @param from Route
 * @param next NavigationGuardNext
 */
export const guardSetup: NavigationGuard = async (
  to: Route,
  from: Route,
  next: NavigationGuardNext<Vue>,
): Promise<void> => {
  const possibleSelectPlanRedirect = await selectPlanRedirect(to, from)
  return next(possibleSelectPlanRedirect)
}

export const guardSelectPlan: NavigationGuard = async (
  to: Route,
  from: Route,
  next: NavigationGuardNext<Vue>,
): Promise<void> => {
  const possibleToSelectPlanRedirects = await prioritizeSetup(to, from)
  return next(possibleToSelectPlanRedirects)
}

export const ensureUserLoggedInAndRedirectToPreviousPath = async (
  to: Route,
  from: Route,
  next: NavigationGuardNext<Vue>,
) => {
  const profileRedirects = await loginRedirects(to, from)
  if (profileRedirects) return next(profileRedirects)
  const comingBackFromWithinTheBrowser: boolean = from.fullPath.startsWith('/drives')
  if (comingBackFromWithinTheBrowser) {
    await store.dispatch('file/clearBrowser')
    return next()
  }

  return next(lastBrowserLocationIfAny())
}

// Used in login / sign-in related, public routes
export const redirectToHomeIfLoggedIn: NavigationGuard = async (to, from, next) => {
  const loggedIn = isLoggedIn()
  if (loggedIn) {
    return next('/dashboard')
  }
  next()
}

// Protects entry onto /complete-profile from people
// not signed up with Google
export const redirectToSignUpIfNotGoogleLoggedIn: NavigationGuard = async (to, from, next) => {
  const { loggedIn } = getCurrentUserInfo()
  if (!loggedIn) {
    return next('/sign-up')
  }
  next()
}

export const restrictAccessToOwnersAndUsers: NavigationGuard = async (to, from, next) => {
  if (store.state.user.profile == null || store.getters.workstations == null) {
    await store.dispatch('user/ensureProfile')
    await store.dispatch('workstation/bindUserWorkstations')
  }

  const uuidToCheck = to.params.pathMatch.slice(1)
  const workstations = store.getters['workstation/workstations']
  const { userId } = getCurrentUserInfo()

  const shouldHaveAccess =
    workstations &&
    workstations.find(
      (ws: WorkstationDocument) =>
        ws.uuid === uuidToCheck && (ws.owner_id === userId || ws.user_id === userId),
    )
  if (shouldHaveAccess) return next()
  return next('404')
}

export const restrictAccessToOwnersAndUsersDrives: NavigationGuard = async (to, from, next) => {
  if (store.state.user.profile == null || store.getters.drives == null) {
    await store.dispatch('user/ensureProfile')
    await store.dispatch('drive/bindUserDrives')
  }

  const uuidToCheck = to.params.pathMatch.slice(0)

  const drives = store.getters['drive/drives']
  const { userId } = getCurrentUserInfo()

  const shouldHaveAccess =
    drives &&
    drives.find(
      (d: DriveDocument) =>
        d.uuid === uuidToCheck && (d.owner_id === userId || d.user_id === userId),
    )
  if (shouldHaveAccess) return next()
  return next('404')
}

// to avoid infinite redirects when redirecting from to private route
export const redirectToLoginIfNotLoggedIn: NavigationGuard = async (to, from, next) => {
  const { loggedIn } = getCurrentUserInfo()
  if (!loggedIn) {
    return next('/')
  }
  store.dispatch('user/ensureProfile')
  store.dispatch('payment/refreshCredits')
  next()
}
